<template lang="pug">
  v-dialog.ui-modal(
    :value="value"
    :max-width="maxWidth"
    :persistent="persistent"
    :eager="eager"
    :content-class="contentClass"
    @input="onInput"
  )
    //- Vuetify's own theme is never switched app-wide (plugins/vuetify.js), so controls rendered
    //- inside a dark modal would keep light-theme colors. The provider hands the dark theme down
    //- to every Vuetify child (fields, icons, pickers) instead of each modal patching colors itself.
    v-theme-provider(:dark="isDarkTheme")
      .ui-modal__dialog
        .ui-modal__header(v-if="hasHeader")
          slot(name="title")
            h2.ui-modal__title(v-if="title" :class="{ 'ui-modal__title--sm': titleSize === 'sm' }") {{ title }}
          v-btn.ui-modal__close(
            v-if="showClose"
            icon
            depressed
            aria-label="Close"
            @click="close"
          )
            UiIcon(name="close" size="24" color="#6F7684")
        .ui-modal__body
          slot(name="description")
            .ui-modal__description(v-if="description" v-html="description")
          slot
        .ui-modal__actions(v-if="actions.length")
          v-btn.ui-modal__action(
            v-for="(action, index) in actions"
            :key="action.key || index"
            depressed
            height="46"
            :style="{ '--ui-modal-action-color': action.color || '#242D3B' }"
            :dark="action.dark !== false"
            :color="action.color || '#242D3B'"
            :outlined="action.variant === 'outlined'"
            :text="action.variant === 'text'"
            :disabled="action.disabled"
            :loading="action.loading"
            @click="runAction(action)"
          ) {{ action.text }}
</template>

<script>
import { mapState } from 'vuex'

export default {
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    actions: { type: Array, default: () => [] },
    maxWidth: { type: [Number, String], default: 480 },
    titleSize: { type: String, default: '' },
    persistent: { type: Boolean, default: false },
    showClose: { type: Boolean, default: true },
    eager: { type: Boolean, default: false }
  },
  computed: {
    ...mapState({
      isDarkTheme: state => state.app.isDarkTheme
    }),
    hasHeader() {
      return Boolean(this.title) || Boolean(this.$slots.title) || this.showClose
    },
    contentClass() {
      const classes = ['ui-modal__content', 'elevation-0']
      if (this.isDarkTheme) classes.push('ui-modal__content--dark')
      return classes.join(' ')
    }
  },
  methods: {
    onInput(value) {
      this.$emit('input', value)
      if (!value) this.$emit('close')
    },
    close() {
      this.onInput(false)
    },
    runAction(action) {
      if (action.closeOnClick !== false) this.close()
      if (typeof action.handler === 'function') action.handler()
    }
  }
}
</script>

<style lang="sass">
$ui-modal-gap: 16px
$ui-modal-padding-x: 24px
$ui-modal-padding-y: 24px
$ui-modal-border-radius: 10px

.ui-modal__content
  .ui-modal__dialog
    display: flex
    flex-direction: column
    gap: $ui-modal-gap
    padding: $ui-modal-padding-y $ui-modal-padding-x
    // Body text color is set once here, so plain markup passed through the slots inherits a
    // readable color in both themes instead of falling back to the light-theme app default.
    color: #101828
    background: #fff
    border-radius: $ui-modal-border-radius
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1)

  .ui-modal__header
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: $ui-modal-gap

  .ui-modal__title
    flex: 1
    margin: 0
    color: #101828
    font-size: 24px
    font-weight: 500
    line-height: 1.2

  .ui-modal__title--sm
    font-size: 18px

  .ui-modal__close
    margin: -8px -8px 0 auto
    flex-shrink: 0

  .ui-modal__body
    display: flex
    flex-direction: column
    gap: $ui-modal-gap

  .ui-modal__description
    color: #667085
    font-size: 16px
    line-height: 1.5

    span
      color: #FF5000
      font-weight: 600

  .ui-modal__actions
    display: flex
    gap: $ui-modal-gap
    margin-top: 8px

  .ui-modal__action
    flex: 1 1 0
    min-width: 0

    // Preserve the action's color in disabled state — Vuetify forces grey with !important
    &.v-btn.v-btn--disabled
      opacity: 0.5

      &.v-btn--has-bg
        background-color: var(--ui-modal-action-color) !important

      &:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined)
        color: #fff !important

      &.v-btn--outlined
        color: var(--ui-modal-action-color) !important
        border-color: var(--ui-modal-action-color) !important

.ui-modal__content--dark
  .ui-modal__dialog
    color: #ECEEF1
    background: #101828

  .ui-modal__title
    color: #fff

  // An outlined action paints its color as ink and border instead of fill, so the dark neutral
  // callers pass for filled buttons (#242D3B) disappears against the dark dialog surface.
  // Override the color at the variable so the disabled rules above stay in sync.
  .ui-modal__action.v-btn--outlined
    --ui-modal-action-color: #ECEEF1 !important
    color: #ECEEF1 !important
    border-color: rgba(236, 238, 241, 0.4) !important

@media (max-width: 540px)
  .ui-modal__content
    .ui-modal__title
      font-size: 18px
</style>
